import { useRef, useState } from "react";
import Button from "Components/UI/Button";
import Input from "Components/UI/Input";
import DetailSection from "Components/PatentDesign/DetailSection";
import FileList from "Components/PatentDesign/FileList";
import { disabledStyle, pdStyles } from "Components/PatentDesign/styles";
import {
  createPatentDesignDocument,
  deletePatentDesignDocument,
  downloadPatentDesignDocument,
} from "JS/API";
import usePatentDesignDownload from "utils/usePatentDesignDownload";
import {
  FILE_ACCEPT,
  TITLE_MAX_LENGTH,
  ERROR_MESSAGE,
  validateUploadFiles,
} from "utils/patentDesign";

const DocumentSection = ({ detail, onReload, onAlert, onError }) => {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);
  const { isDownloading, download } = usePatentDesignDownload({ onAlert, onError });

  const documents = (detail.documents || []).map((doc) => ({
    id: doc.id,
    title: doc.title,
    name: doc.original_file_name,
    size: doc.file_size,
    createdAt: doc.created_at,
  }));

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0] || null;
    if (selected) {
      const error = validateUploadFiles([selected], 1);
      if (error) {
        e.target.value = "";
        setFile(null);
        onAlert(ERROR_MESSAGE[error]);
        return;
      }
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const trimmed = title.trim();
    if (!trimmed) {
      onAlert("서류 제목을 입력해 주세요.");
      return;
    }
    if (trimmed.length > TITLE_MAX_LENGTH) {
      onAlert(`서류 제목은 ${TITLE_MAX_LENGTH}자 이하로 입력해 주세요.`);
      return;
    }
    if (!file) {
      onAlert("첨부할 파일을 선택해 주세요.");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await createPatentDesignDocument(detail.id, { title: trimmed, file });
      if (result && typeof result === "object") {
        setTitle("");
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        onAlert("고객용 서류를 첨부했습니다.");
        onReload();
      } else if (ERROR_MESSAGE[result]) {
        onAlert(ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (doc) => {
    if (deletingId) return;
    if (!window.confirm(`'${doc.title}' 서류를 삭제할까요? 고객 화면에서도 사라집니다.`)) return;
    setDeletingId(doc.id);
    try {
      const result = await deletePatentDesignDocument(doc.id);
      if (result && typeof result === "object") {
        onAlert("서류를 삭제했습니다.");
        onReload();
      } else if (ERROR_MESSAGE[result]) {
        onAlert(ERROR_MESSAGE[result]);
      } else {
        onError(result);
      }
    } catch {
      onError();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <DetailSection title="고객용 서류">
      <div style={pdStyles.form}>
        <div style={pdStyles.row}>
          <div style={{ flex: 1, minWidth: "200px" }}>
            <label htmlFor="patent-design-document-title" style={pdStyles.label}>
              서류 제목
            </label>
            <Input
              id="patent-design-document-title"
              maxLength={TITLE_MAX_LENGTH}
              placeholder="예) 출원서 사본"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="patent-design-document-file" style={pdStyles.label}>
              서류 파일
            </label>
            <input
              id="patent-design-document-file"
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPT}
              onChange={handleFileChange}
            />
          </div>
        </div>
        <div>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={disabledStyle(isSubmitting)}
          >
            서류 첨부
          </Button>
        </div>
      </div>

      <FileList
        files={documents}
        disabled={isDownloading || Boolean(deletingId)}
        emptyText="첨부한 서류가 없어요"
        onDownload={(doc) => download([doc], downloadPatentDesignDocument)}
        onDelete={handleDelete}
      />
    </DetailSection>
  );
};

export default DocumentSection;
